import * as Popover from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHotkey } from "@/shared/hooks/use-hotkey";
import { cn } from "@/shared/ui";
import type { MyCatalog } from "@/shared/api/schemas";
import { myCatalogsQuery } from "./queries";

/**
 * Catalog switcher: Radix Popover + filterable list of the user's catalogs.
 * Trigger shows the current catalog name (or "Select catalog" when none).
 * Keyboard: ⌘J toggles, ↑/↓ navigate, Enter selects, Esc closes.
 */
export function CatalogSwitcher({ currentName }: { currentName?: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useHotkey("mod+j", () => setOpen((o) => !o));

  const { data: catalogs = [] } = useQuery(myCatalogsQuery);
  const current = useMemo(
    () => catalogs.find((c) => c.name === currentName),
    [catalogs, currentName],
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Switch catalog"
          className={cn(
            "group flex w-full items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus)]",
          )}
        >
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full bg-[var(--accent)]"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-[var(--fg)]">
              {current?.label ?? currentName ?? "Select catalog"}
            </span>
            {current && (
              <span className="block truncate text-[10px] uppercase tracking-wider text-[var(--fg-muted)]">
                {current.name} · {current.perm}
              </span>
            )}
          </span>
          <kbd className="hidden shrink-0 rounded border border-[var(--border)] bg-[var(--bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--fg-muted)] group-hover:inline">
            ⌘J
          </kbd>
          <ChevronIcon />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[calc(var(--radix-popover-trigger-width))] rounded-lg border border-[var(--border)] bg-[var(--surface)] p-1 shadow-[var(--shadow-2)]"
        >
          <SwitcherList
            catalogs={catalogs}
            {...(currentName ? { currentName } : {})}
            onPick={(c) => {
              setOpen(false);
              navigate({ to: "/catalogs/$name", params: { name: c.name } });
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function SwitcherList({
  catalogs,
  currentName,
  onPick,
}: {
  catalogs: MyCatalog[];
  currentName?: string;
  onPick: (c: MyCatalog) => void;
}) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return catalogs;
    return catalogs.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) || c.label.toLowerCase().includes(needle),
    );
  }, [catalogs, q]);

  useEffect(() => {
    setActive(0);
  }, [q]);

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const picked = filtered[active];
      if (picked) onPick(picked);
    }
  }

  return (
    <div className="flex flex-col">
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        onKeyDown={onKey}
        placeholder="Filter catalogs…"
        className="h-9 rounded-md bg-transparent px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)]/60 focus:outline-none"
        aria-label="Filter catalogs"
      />
      <div className="my-1 h-px bg-[var(--border)]" />
      {filtered.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-[var(--fg-muted)]">
          No catalogs match.
        </p>
      ) : (
        <ul role="listbox" className="max-h-72 overflow-y-auto">
          {filtered.map((c, i) => {
            const isActive = i === active;
            const isCurrent = c.name === currentName;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => onPick(c)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                    isActive ? "bg-[var(--surface-2)]" : "hover:bg-[var(--surface-2)]",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      isCurrent ? "bg-[var(--accent)]" : "bg-[var(--border)]",
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{c.label}</span>
                    <span className="block truncate text-[10px] uppercase tracking-wider text-[var(--fg-muted)]">
                      {c.name}
                    </span>
                  </span>
                  <span className="shrink-0 rounded bg-[var(--surface)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--fg-muted)]">
                    {c.perm}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg
      aria-hidden
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="shrink-0 text-[var(--fg-muted)]"
    >
      <path d="m7 9 5-5 5 5M7 15l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
